import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { CompositeModel } from '../complex/composite.model';
import { TextModel } from '../primitive/text.model';
import { assignModelOptions, CrmCodeModel, isDefined, NumberModel } from '@evorait/pace-core';
import moment from 'moment/moment';

export class RadioActiveModel extends CompositeModel {
    public location: TextModel;
    public meterOrCpm: NumberModel;
    public net: NumberModel;
    public dpm: NumberModel;
    public isEditable = false;
    public isDeletable = false;

    public constructor(oDataLocation = '') {
        super('MeasurementToolModel');
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        // Manually added entry can be deleted and edited
        this.isDeletable = !oDataLocation;
        this.isEditable = !oDataLocation;

        this.location = <TextModel>this.set(new TextModel('Location', oDataLocation));
        modelOptionsLoader.load(this.location, 'RadioActive', 'Location');
        this.location = assignModelOptions(
            this.location,
            {
                ...this.location.options,
                readonly: !!oDataLocation,
            },
            this,
        );

        this.meterOrCpm = <NumberModel>this.set(new NumberModel('meterOrCpm', undefined));
        modelOptionsLoader.load(this.meterOrCpm, 'RadioActive', 'meterOrCpm');

        this.net = <NumberModel>this.set(new NumberModel('net', undefined));
        modelOptionsLoader.load(this.net, 'RadioActive', 'net');

        this.dpm = <NumberModel>this.set(new NumberModel('dpm', undefined));
        modelOptionsLoader.load(this.dpm, 'RadioActive', 'dpm');
        this.calculateAndUpdateDPM();
    }

    public static fromOData(location: string): RadioActiveModel {
        return new RadioActiveModel(location);
    }

    public getRadioActiveSurveyCrmCodeModel(): CrmCodeModel {
        const codeText = `${this.location.value};${this.meterOrCpm.value};${this.net.value};${this.dpm.value}`;

        return new CrmCodeModel({
            codeType: 'A',
            codeGroup: 'SURV',
            code: 'RSR',
            codeText: codeText,
            startDateTime: moment().startOf('minute'),
            endDateTime: moment().startOf('minute'),
        });
    }

    public calculateAndUpdateDPM(calibrationValue?: number): void {
        if (isDefined(calibrationValue)) {
            // Use .toFixed(2) to limit the result to two decimal place (e.g., 12.6666666 → 12.66)
            // If Net is cleared, dpm becomes 0 and the field wrongly turns valid.
            // Set dpm undefined to keep it empty and invalid.
            this.dpm.value = isDefined(this.net.value)
                ? Number((this.net.value / calibrationValue).toFixed(2))
                : undefined;
        }
        this.dpm = assignModelOptions(
            this.dpm,
            {
                ...this.dpm.options,
                allowReadonlyValidation: true,
                required: !this.dpm.value,
            },
            this,
        );
    }

    public toJSON(): JsonRadioActiveModel {
        return {
            location: this.location.value,
            meterOrCpm: this.meterOrCpm.value,
            net: this.net.value,
            dpm: this.dpm.value,
            isEditable: this.isEditable,
            isDeletable: this.isDeletable,
        };
    }
}

export class RadioActiveModelFromJson extends RadioActiveModel {
    public constructor(data: JsonRadioActiveModel) {
        super();
        this.location.value = data.location;
        this.net.value = data.net;
        this.meterOrCpm.value = data.meterOrCpm;
        this.isDeletable = data.isDeletable;
        this.isEditable = data.isEditable;
        this.dpm.value = data.dpm;
        this.location = assignModelOptions(
            this.location,
            {
                ...this.location.options,
                readonly: !data.isEditable,
            },
            this,
        );
        this.calculateAndUpdateDPM();
    }
}

export interface JsonRadioActiveModel {
    location: string;
    meterOrCpm: number | undefined;
    net: number | undefined;
    dpm: number | undefined;
    isEditable: boolean;
    isDeletable: boolean;
}
